import React, {useCallback, useEffect, useState} from 'react';
import {AccountInfoItem, Origination} from '../../../../stores/types';
import {loadAccountById, triggerRefund} from '../../../../stores/ApiHelper';
import {formatCurrency} from '../../../../stores/JSHelper';
import Psp from '../../../../components/widget/Psp';

type RefundModalProps = {
  account: AccountInfoItem;
  onClose: () => void;
  onSuccess: () => void;
};

type ModalStep = 'loading' | 'confirm' | 'triggering' | 'success' | 'error';

const InfoRow: React.FC<{label: string; value: React.ReactNode; bold?: boolean}> = ({label, value, bold}) => (
  <div className="flex items-center justify-between py-[6px]">
    <span className="text-[13px] text-gray-400">{label}</span>
    <span className={`text-[13px] text-navy-700 ${bold ? 'font-bold' : 'font-medium'}`}>{value}</span>
  </div>
);

const SectionLabel: React.FC<{children: React.ReactNode}> = ({children}) => (
  <p className="mb-1 mt-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{children}</p>
);

const RefundModal: React.FC<RefundModalProps> = ({account, onClose, onSuccess}) => {
  const [step, setStep] = useState<ModalStep>('loading');
  const [refundOrigination, setRefundOrigination] = useState<Origination | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refundResult, setRefundResult] = useState<{refundId: string; amount: number} | null>(null);

  const availableBalance = account.availableWalletBalance?.availableBalance ?? 0;

  useEffect(() => {
    (async () => {
      try {
        const fullAccount = await loadAccountById(account.accountId);
        const refundOrig = fullAccount.originations?.find((o) => o.isDefaultRefundAccount);
        if (refundOrig) {
          setRefundOrigination(refundOrig);
          setStep('confirm');
        } else {
          setError('No default refund account configured for this buyer.');
          setStep('error');
        }
      } catch (err: any) {
        setError(typeof err === 'string' ? err : 'Failed to load account details.');
        setStep('error');
      }
    })();
  }, [account.accountId]);

  const handleTriggerRefund = useCallback(async () => {
    setStep('triggering');
    setError(null);
    try {
      const result = await triggerRefund(account.accountId);
      setRefundResult({refundId: result.refundId, amount: result.amount});
      setStep('success');
    } catch (err: any) {
      setError(typeof err === 'string' ? err : 'Failed to trigger refund.');
      setStep('confirm');
    }
  }, [account.accountId]);

  return (
    <div className="relative z-50" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm transition-opacity"></div>
      <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">

            {/* Loading */}
            {step === 'loading' && (
              <div className="flex flex-col items-center justify-center px-6 py-16">
                <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-gray-200 border-t-brand-500"></div>
                <p className="mt-4 text-sm text-gray-400">Loading account details...</p>
              </div>
            )}

            {/* Confirm */}
            {step === 'confirm' && (
              <>
                <div className="px-6 pt-6 pb-5">
                  {/* Header */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-50">
                      <svg className="h-5 w-5 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                    </div>
                    <div>
                      <h6 className="text-[16px] font-bold text-navy-700">Confirm Refund</h6>
                      <p className="text-[12px] text-gray-400">Full available balance will be transferred</p>
                    </div>
                  </div>

                  {refundOrigination ? (
                    <div className="mt-5">
                      {/* Source Account */}
                      <SectionLabel>Buyer's Virtual Account</SectionLabel>
                      <div className="rounded-xl bg-gray-50 px-4 py-2">
                        <InfoRow label="Name" value={account.name} />
                        <InfoRow label="Account ID" value={account.accountId} />
                        {account.externalId && <InfoRow label="External ID" value={account.externalId} />}
                        {account.pspType && (
                          <InfoRow label="PSP" value={
                            <span className="flex items-center gap-1.5">
                              <Psp pspType={account.pspType} empty />
                              <span className="capitalize">{account.pspType}</span>
                            </span>
                          } />
                        )}
                      </div>

                      {/* Destination */}
                      <SectionLabel>Refund Destination</SectionLabel>
                      <div className="rounded-xl bg-gray-50 px-4 py-2">
                        <InfoRow label="Origination" value={refundOrigination.name} />
                        {refundOrigination.externalSource && (
                          <InfoRow label="Source" value={
                            <span className="capitalize">{refundOrigination.externalSource}</span>
                          } />
                        )}
                        {refundOrigination.externalId && (
                          <InfoRow label="Vendor ID" value={refundOrigination.externalId} />
                        )}
                        {refundOrigination.metadata?.accountNumber && (
                          <InfoRow label="Bank Account" value={refundOrigination.metadata.accountNumber} />
                        )}
                        {refundOrigination.metadata?.ifscCode && (
                          <InfoRow label="IFSC" value={refundOrigination.metadata.ifscCode} />
                        )}
                        {refundOrigination.metadata?.vpa && (
                          <InfoRow label="VPA / UPI" value={refundOrigination.metadata.vpa} />
                        )}
                      </div>

                      {/* Amount */}
                      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[13px] font-medium text-amber-700">Refund Amount</span>
                          <span className="text-xl font-bold text-navy-700">{formatCurrency(availableBalance, 'INR', false)}</span>
                        </div>
                      </div>

                      {/* Warning */}
                      <p className="mt-3 text-center text-[11px] text-gray-400">
                        This action is irreversible. The full available balance will be transferred to the refund account.
                      </p>
                    </div>
                  ) : null}

                  {/* Inline error — shown when trigger API fails */}
                  {error && (
                    <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                      <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                      </svg>
                      <div>
                        <p className="text-[13px] font-medium text-red-700">{error}</p>
                        <p className="mt-0.5 text-[11px] text-red-400">Please try again or contact support if this persists.</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 border-t border-gray-100 bg-gray-50/50 px-6 py-4">
                  <button
                    onClick={onClose}
                    type="button"
                    className="flex-1 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200 transition duration-200 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  {refundOrigination && (
                    <button
                      onClick={handleTriggerRefund}
                      type="button"
                      className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition duration-200 hover:bg-red-700 active:bg-red-800"
                    >
                      Confirm Refund
                    </button>
                  )}
                </div>
              </>
            )}

            {/* Error — full state for config/load failures */}
            {step === 'error' && (
              <>
                <div className="px-6 pt-6 pb-5">
                  <div className="flex flex-col items-center text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                      <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                    </div>
                    <h6 className="mt-3 text-[16px] font-bold text-navy-700">Refund Unavailable</h6>
                    <p className="mt-2 max-w-[280px] text-[13px] leading-5 text-gray-500">{error}</p>
                  </div>

                  <div className="mt-5 rounded-xl bg-gray-50 px-4 py-3">
                    <InfoRow label="Account" value={account.name} />
                    <InfoRow label="Account ID" value={account.accountId} />
                    <InfoRow label="Available" value={formatCurrency(availableBalance, 'INR', false)} />
                  </div>

                  <p className="mt-3 text-center text-[11px] text-gray-400">
                    To configure a refund account, set <span className="font-medium text-gray-500">isDefaultRefundAccount</span> on the origination for this buyer.
                  </p>
                </div>

                <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-4">
                  <button
                    onClick={onClose}
                    type="button"
                    className="w-full rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200 transition duration-200 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </>
            )}

            {/* Triggering */}
            {step === 'triggering' && (
              <div className="flex flex-col items-center justify-center px-6 py-16">
                <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-gray-200 border-t-brand-500"></div>
                <p className="mt-4 text-sm font-medium text-navy-700">Processing refund...</p>
                <p className="mt-1 text-[12px] text-gray-400">Please do not close this window</p>
              </div>
            )}

            {/* Success */}
            {step === 'success' && refundResult && (
              <>
                <div className="px-6 pt-6 pb-5">
                  <div className="flex flex-col items-center text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                      <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    </div>
                    <h6 className="mt-3 text-[16px] font-bold text-navy-700">Refund Initiated</h6>
                    <p className="mt-1 text-[12px] text-gray-400">The refund has been submitted for processing</p>
                  </div>

                  <div className="mt-5 rounded-xl bg-green-50 px-4 py-3">
                    <InfoRow label="Refund ID" value={refundResult.refundId} bold />
                    <InfoRow label="Amount" value={formatCurrency(refundResult.amount, 'INR', false)} bold />
                    <InfoRow label="Account" value={account.name} />
                    <InfoRow label="Status" value={
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">Initiated</span>
                    } />
                  </div>
                </div>

                <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-4">
                  <button
                    onClick={onSuccess}
                    type="button"
                    className="w-full rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition duration-200 hover:bg-brand-600 active:bg-brand-700"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundModal;
